import React from 'react';
import { Card, CardProps, styled } from '@mui/material';

interface GlassCardProps extends Omit<CardProps, 'variant'> {
  variant?: 'default' | 'elevated' | 'interactive' | 'subtle' | 'error';
  glowColor?: string;
}

interface StyledGlassCardProps extends Omit<CardProps, 'variant'> {
  glowColor?: string;
  $variant: 'default' | 'elevated' | 'interactive' | 'subtle' | 'error';
}

const StyledGlassCard = styled(Card)<StyledGlassCardProps>(({ theme, glowColor, $variant }) => {
  const baseStyles = {
    background: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 16,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    // Accessibility improvements
    '&:focus-within': {
      outline: '2px solid rgba(255, 107, 53, 0.5)',
      outlineOffset: 2,
    },
    // High contrast mode support
    '@media (prefers-contrast: high)': {
      border: '2px solid rgba(255, 255, 255, 0.5)',
      background: 'rgba(255, 255, 255, 0.25)',
    },
    // Reduced motion support
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  };

  const variants = {
    default: {
      ...baseStyles,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    },
    elevated: {
      ...baseStyles,
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
      transform: 'translateY(-2px)',
    },
    interactive: {
      ...baseStyles,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
        border: `1px solid ${glowColor || theme.palette.primary.main}33`,
        '&::before': {
          opacity: 1,
        },
      },
      '&::before': {
        content: '""',
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 16,
        padding: 1,
        background: `linear-gradient(135deg, ${glowColor || theme.palette.primary.main}33, ${theme.palette.secondary.main}33)`,
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'xor',
        WebkitMaskComposite: 'xor',
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
      position: 'relative' as const,
    },
    subtle: {
      ...baseStyles,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      background: 'rgba(255, 255, 255, 0.08)',
    },
    error: {
      ...baseStyles,
      boxShadow: '0 8px 32px rgba(211, 47, 47, 0.3)',
      background: 'rgba(211, 47, 47, 0.1)',
      border: '1px solid rgba(211, 47, 47, 0.3)',
    },
  };

  return variants[$variant as keyof typeof variants];
});

export const GlassCard: React.FC<GlassCardProps> = ({ 
  children, 
  variant = 'default',
  glowColor,
  ...props 
}) => {
  return (
    <StyledGlassCard glowColor={glowColor} $variant={variant} {...props}>
      {children}
    </StyledGlassCard>
  );
};